import { Component, DestroyRef, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { Textarea } from 'primeng/textarea';
import { InputNumber } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Subject, filter } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { CrossReferenceService } from '../../../services/mbrs/cross-reference.service';
import { CalculationService } from '../../../services/mbrs/calculation.service';
import { CrossTabLayoutChangeNotifierService } from '../../../services/mbrs/cross-tab-layout-change-notifier.service';
import { NegativeHighlightDirective } from '../../../directives/negative-highlight.directive';
import { SchemaNumericDirective } from '../../../directives/schema-numeric.directive';
import { NumericFieldPolicy, resolveNumericPolicy } from '../../../utils/numeric-policy.util';
import { Router } from '@angular/router';
import { ReloadPromptComponent } from '../../common/reload-prompt/reload-prompt.component';
import { ReloadBannerComponent } from '../../common/reload-banner/reload-banner.component';
import { stripAllValidatorsRecursively } from '../../../utils/form-validation.util';
import {
  FORM_CALCULATION_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import { buildFormEditPipeline } from '../../../utils/form-edit-pipeline.util';
import { mapSchemaValidators } from '../../../utils/schema-validators.util';
// Shared business rules — single source of truth used by both this
// component and the pure validator. Prevents the editor and the
// orchestrator from drifting on column-suffix matrix and
// consolidated/comparative field names. See
// services/mbrs/validators/role11-sub-classification-liquidity.rules.ts.
import {
  resolveRole11ColumnSuffixes,
  ROLE11_COMPARATIVE_FIELD,
  ROLE11_COMPARATIVE_PRESENTED_VALUES,
  ROLE11_CONSOLIDATED_FIELD,
  ROLE11_CONSOLIDATED_VALUE,
} from '../../../services/mbrs/validators/role11-sub-classification-liquidity.rules';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { ExtractedSumWarningService, EXTRACTED_SUM_MARKER } from '../../../services/mbrs/extracted-sum-warning.service';
import { SummableControl } from '../../../services/mbrs/extracted-sum-warning';
import { ExtractedSumBaselineDebugService } from '../../../services/common/extracted-sum-baseline-debug.service';
import {
  TOAST_DURATION_DEFAULT_MS,
  TOAST_DURATION_LONG_MS,
} from '../../../utils/toast-durations.util';
import { PlaceholderPipe } from '../../../utils/pipes/placeholder.pipe';
import { isControlMandatory } from '../../../utils/required-field.util';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { FieldLinkTargetDirective } from '../../../directives/field-link-target.directive';
import { computedControlPredicate, revealValidationErrors } from '../../../utils/validation-reveal.util';
import { adoptIntoAutoSaveSheet } from '../../../utils/adopt-remote-sheet-state.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
interface FormSchema {
  role: string;
  definition: string;
  sheetId: string;
  label: string;
  controls: FormControlSchema[];
}

interface FormControlSchema {
  qname: string;
  label: string;
  type: string;
  ui: {
    control: string;
    placeholder?: string;
    enumSource?: string;
    readonly?: boolean;
    calculated?: boolean;
    defaultValue?: string;
    rows?: number;
    level?: string;
    dualColumn?: boolean;
  };
  required?: boolean;
  validators?: Array<{ name: string; value?: number }>;
}

@Component({
  selector: 'app-role11-220100-sub-classification-liquidity',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputTextModule,
    Textarea,
    InputNumber,
    ButtonModule,
    TooltipModule,
    Toast,
    NegativeHighlightDirective,

    SchemaNumericDirective,
    ReloadPromptComponent,
    ReloadBannerComponent,
    PlaceholderPipe,
    FieldLinkTargetDirective
  ],
  providers: [MessageService],
  templateUrl: './role11-220100-sub-classification-liquidity.component.html',
  styleUrls: ['./role11-220100-sub-classification-liquidity.component.css']
})
export class Role11220100SubClassificationLiquidityComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);
  private stateService = inject(FilingFormStateService);
  private crossRefService = inject(CrossReferenceService);
  private calculationService = inject(CalculationService);
  private layoutChangeNotifier = inject(CrossTabLayoutChangeNotifierService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private extractedSumWarning = inject(ExtractedSumWarningService);
  private baselineDebug = inject(ExtractedSumBaselineDebugService);
  private validationErrors = new Map<string, string[]>();

  /** Word-suffix -> human label for the Error Description column. */
  private readonly ROLE11_SUFFIX_LABELS: { key: string; label: string }[] = [
    { key: 'GroupCurrent', label: 'Group Current' },
    { key: 'GroupPrevious', label: 'Group Previous' },
    { key: 'CompanyCurrent', label: 'Company Current' },
    { key: 'CompanyPrevious', label: 'Company Previous' },
    { key: 'Current', label: 'Current' },
    { key: 'Previous', label: 'Previous' },
  ];

  private readonly sheetKey = SHEET_KEYS.subClassificationLiquidity;

  subClassificationLiquidityForm!: FormGroup;
  schema!: FormSchema;
  isLoading = true;
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Sub-classification (Liquidity Order) (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.subClassificationLiquidity
    );
  }

  // Track if consolidated and has comparative info (from Role-2)
  isConsolidated = false;
  hasPreviousYear = false;

  showReloadPrompt = signal<boolean>(false);
  reloadPromptDismissed = signal<boolean>(false);

  /**
   * Get currency symbol with unit from Role-1
   * e.g., "RM'000", "$'000", "€'000"
   */
  get currencyUnit(): string {
    const symbol = this.crossRefService.getCurrencySymbol();
    return `${symbol}'000`;
  }

  /**
   * Get period header for current year
   * e.g., "For the year ended 31 December 2024"
   */
  get periodHeaderCurrent(): string {
    return this.crossRefService.getPeriodHeader('current');
  }

  /**
   * Get period header for previous year
   * e.g., "For the year ended 31 December 2023"
   */
  get periodHeaderPrevious(): string {
    return this.crossRefService.getPeriodHeader('previous');
  }

  private isLoadingState = false;
  private isCalculating = false;

  async ngOnInit(): Promise<void> {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '11',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    // This sheet does not await the container's MBRS hydration, so step 1
    // has nothing to wait for and is satisfied on arrival.
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);
    this.baselineDebug.bindGlobalShortcut(this.destroyRef);
    try {
      // Check consolidated status from Role-2
      this.checkConsolidatedStatus();

      await this.loadSchema();
      this.buildForm();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
      this.setupEditPipeline();

      // Reveal the sheet's already-computed errors now that the form exists
      // and has been populated. Deterministic replacement for the filing-form
      // container's setTimeout-driven markAllAsTouched, which silently no-ops
      // when a sheet's async init has not yet assigned its FormGroup — see
      // revealValidationErrors' docblock. Touch-only: cannot move the verdict.
      revealValidationErrors(this.subClassificationLiquidityForm, {
        isComputed: computedControlPredicate(this.schema?.controls),
      });
      this.loadSavedState();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
      this.setupLayoutReactivity();
      this.setupCrossTabLiveAdoption();
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    } catch (error) {
      // Report through the trace instead of a fixed sentence. The toast now
      // names this sheet, the steps it reached, and whether the schema was
      // missing, unreadable, or fine but the form could not be built —
      // `classifySheetInitError` infers the last of those by elimination.
      // Must run BEFORE the loading flag is cleared below: `fail()` closes the
      // trace, which is what stops the finalize mark on the next line from
      // recording this failed init as a success.
      this.loadTraceHandle.fail(classifySheetInitError(error), error);
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Check if financial statements are consolidated and have comparative information (from Role-2)
   */
  private checkConsolidatedStatus(): void {
    const scopeOfFilingData = this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling);
    this.isConsolidated =
      scopeOfFilingData?.[ROLE11_CONSOLIDATED_FIELD] === ROLE11_CONSOLIDATED_VALUE;

    const comparativeInfo = scopeOfFilingData?.[ROLE11_COMPARATIVE_FIELD];
    this.hasPreviousYear =
      typeof comparativeInfo === 'string' &&
      ROLE11_COMPARATIVE_PRESENTED_VALUES.includes(comparativeInfo);

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Consolidated status:', this.isConsolidated);
    // console.log('Has previous year:', this.hasPreviousYear);
  }

  // Why: pure predicate, no mutation of isConsolidated / hasPreviousYear.
  // Under the reload-prompt model the component's layout MUST stay frozen
  // until the user reloads — mutating the flags here would break the
  // schema/data invariant the cached FormGroup relies on. Compare-only,
  // return boolean, let the notifier surface the prompt.
  private detectLayoutFlagsChanged(): boolean {
    const scopeOfFilingData = this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling);
    const freshConsolidated =
      scopeOfFilingData?.[ROLE11_CONSOLIDATED_FIELD] === ROLE11_CONSOLIDATED_VALUE;
    const comparativeInfo = scopeOfFilingData?.[ROLE11_COMPARATIVE_FIELD];
    const freshPreviousYear =
      typeof comparativeInfo === 'string' &&
      ROLE11_COMPARATIVE_PRESENTED_VALUES.includes(comparativeInfo);

    return (
      freshConsolidated !== this.isConsolidated ||
      freshPreviousYear !== this.hasPreviousYear
    );
  }

  private router = inject(Router);

  private setupLayoutReactivity(): void {
    this.layoutChangeNotifier
      .watchSubmissionCommitted({
        sheetKey: this.sheetKey,
        slicesOfInterest: [SHEET_KEYS.filingPrerequisites, SHEET_KEYS.filingInfo, SHEET_KEYS.scopeOfFiling],
        detectChanged: () => this.detectLayoutFlagsChanged(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.reloadPromptDismissed()) {
          this.showReloadPrompt.set(true);
        }
      });
  }

  /**
   * Cross-tab live adoption — when a sibling tab edits THIS sheet's own
   * subClassificationLiquidity slice, `applyRemoteChange` rehydrates the
   * shared state and publishes `changedSheets` on `remoteUpdateApplied$`.
   * `adoptRemoteState()` below repaints this sheet's form from that state
   * (silently, no emitted valueChanges).
   */
  private setupCrossTabLiveAdoption(): void {
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(({ changedSheets }) => changedSheets.includes(SHEET_KEYS.subClassificationLiquidity)),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.subClassificationLiquidity)),
        filter(() => !!this.schema && !!this.subClassificationLiquidityForm),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.adoptRemoteState();
      });
  }

  /**
   * Cross-tab adoption: a sibling tab edited this sheet, `applyRemoteChange` has
   * already rehydrated the shared state, and this repaints the form from it.
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoAutoSaveSheet`.
   * The validation tail below runs for DISPLAY only; the sibling already
   * persisted its own results.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    adoptIntoAutoSaveSheet(
      this.subClassificationLiquidityForm,
      this.stateService.getSheetData(SHEET_KEYS.subClassificationLiquidity)
    );
    this.calculateTotals();
  }

  onReload(): void {
    window.location.reload();
  }

  onDismiss(): void {
    this.showReloadPrompt.set(false);
    this.reloadPromptDismissed.set(true);
    this.disableAllValidations();
  }

  onReturnToFiles(): void {
    this.showReloadPrompt.set(false);
    this.router.navigate(['/mbrs/xbrl-files']);
  }

  // Why: one-way door. Once the user dismisses the reload prompt the
  // in-memory form schema is known-stale vs. upstream scope; persisting
  // any further validation result would be misleading. We strip every
  // validator from every control (and the root FormGroup) so no spurious
  // errors accumulate before the user reloads. There is deliberately no
  // re-enable path — a fresh ngOnInit (via reload) is the only recovery.
  private disableAllValidations(): void {
    stripAllValidatorsRecursively(this.subClassificationLiquidityForm);
  }

  private async loadSchema(): Promise<void> {
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    this.schema = await fetchSheetSchema<FormSchema>(
      this.loadTraceHandle,
      '/assets/forms/role-220100.form.json',
    );
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Schema loaded:', this.schema);
  }

  /**
   * Build the reactive form from schema with dual-column support
   */
  private buildForm(): void {
    if (!this.schema || !this.schema.controls) {
      console.error('Schema not loaded - cannot build form');
      throw new Error('Schema must be loaded before building form');
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Building form with schema:', this.schema);
    const formControls: { [key: string]: FormControl<any> } = {};

    this.schema.controls.forEach((control: FormControlSchema) => {
      // Skip section headers
      if (control.type === 'SectionHeader') {
        return;
      }

      const validators = [];

      // Add required validator
      if (control.required) {
        validators.push(Validators.required);
      }

      // Numeric/format constraints (min/max/pattern) from the schema, derived
      // per control via the shared util — see schema-validators.util.
      validators.push(...mapSchemaValidators(control));

      // Get default value
      let defaultValue: any = null;
      if (control.ui.defaultValue) {
        defaultValue = control.ui.defaultValue;
      }

      // Create controls for monetary fields with dynamic column support
      if (control.ui.dualColumn) {
        if (this.isConsolidated && this.hasPreviousYear) {
          // 4 columns: Group Current, Group Previous, Company Current, Company Previous
          formControls[control.qname + 'GroupCurrent'] = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
            validators
          );
          formControls[control.qname + 'GroupPrevious'] = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
            validators
          );
          formControls[control.qname + 'CompanyCurrent'] = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
            validators
          );
          formControls[control.qname + 'CompanyPrevious'] = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
            validators
          );
        } else if (this.isConsolidated && !this.hasPreviousYear) {
          // 2 columns: Group Current, Company Current
          formControls[control.qname + 'GroupCurrent'] = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
            validators
          );
          formControls[control.qname + 'CompanyCurrent'] = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
            validators
          );
        } else if (!this.isConsolidated && this.hasPreviousYear) {
          // 2 columns: Current Year, Previous Year
          formControls[control.qname + 'Current'] = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
            validators
          );
          formControls[control.qname + 'Previous'] = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
            validators
          );
        } else {
          // 1 column: Current Year only
          formControls[control.qname + 'Current'] = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
            validators
          );
        }
      } else {
        // Single control for non-monetary fields
        const formControl = new FormControl(
          { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
          validators
        );
        formControls[control.qname] = formControl;
      }
    });

    this.subClassificationLiquidityForm = new FormGroup(formControls);
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Form built with controls:', Object.keys(formControls));

    // Disable calculated fields to prevent manual editing
    this.disableCalculatedFields();
  }

  /**
   * Disable calculated fields programmatically
   * Called after form creation to prevent "changed after checked" warnings
   */
  private disableCalculatedFields(): void {
    if (!this.schema) return;

    this.schema.controls.forEach(control => {
      if (this.isCalculatedField(control)) {
        // Disable all column variants for this calculated field
        if (this.isConsolidated && this.hasPreviousYear) {
          this.subClassificationLiquidityForm.get(control.qname + 'GroupCurrent')?.disable({ emitEvent: false });
          this.subClassificationLiquidityForm.get(control.qname + 'GroupPrevious')?.disable({ emitEvent: false });
          this.subClassificationLiquidityForm.get(control.qname + 'CompanyCurrent')?.disable({ emitEvent: false });
          this.subClassificationLiquidityForm.get(control.qname + 'CompanyPrevious')?.disable({ emitEvent: false });
        } else if (this.isConsolidated && !this.hasPreviousYear) {
          this.subClassificationLiquidityForm.get(control.qname + 'GroupCurrent')?.disable({ emitEvent: false });
          this.subClassificationLiquidityForm.get(control.qname + 'CompanyCurrent')?.disable({ emitEvent: false });
        } else if (!this.isConsolidated && this.hasPreviousYear) {
          this.subClassificationLiquidityForm.get(control.qname + 'Current')?.disable({ emitEvent: false });
          this.subClassificationLiquidityForm.get(control.qname + 'Previous')?.disable({ emitEvent: false });
        } else {
          this.subClassificationLiquidityForm.get(control.qname + 'Current')?.disable({ emitEvent: false });
        }
      }
    });
  }

  /**
   * Wire the single debounced edit pipeline: calc → persist (no separate validate).
   */
  private setupEditPipeline(): void {
    buildFormEditPipeline({
      source$: this.subClassificationLiquidityForm.valueChanges,
      debounceMs: FORM_CALCULATION_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.isLoadingState || this.isCalculating,
      calculate: () => this.calculateTotals(),
      persist: () => this.stateService.updateSheetData(
        SHEET_KEYS.subClassificationLiquidity,
        this.subClassificationLiquidityForm.value,
        true
      ),
    });
  }

  /**
   * Calculate all totals in the sub-classification (all column combinations)
   */
  private calculateTotals(): void {
    this.isCalculating = true;

    // Determine suffixes based on consolidation and comparative status.
    // Shared with the pure validator via resolveRole11ColumnSuffixes —
    // single source of truth for the column-visibility matrix.
    const suffixes: ReadonlyArray<string> = resolveRole11ColumnSuffixes(
      this.isConsolidated,
      this.hasPreviousYear
    );

    const calculatedTotals = this.calculationService.calculateTotals_220100(
      this.subClassificationLiquidityForm.getRawValue(),
      suffixes
    );

    Object.keys(calculatedTotals).forEach((controlName) => {
      this.subClassificationLiquidityForm
        .get(controlName)
        ?.setValue(calculatedTotals[controlName], { emitEvent: false });
    });

    this.stateService.registerCalculatedKeys(
      SHEET_KEYS.subClassificationLiquidity,
      Object.keys(calculatedTotals)
    );

    this.validateAgainstApiData();

    this.isCalculating = false;
  }

  /**
   * Load saved state from state service
   */
  private loadSavedState(): void {
    this.isLoadingState = true;
    this.stateService.markSheetInitializing(SHEET_KEYS.subClassificationLiquidity);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.subClassificationLiquidity);

    if (savedData && Object.keys(savedData).length > 0) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('Loading saved state:', savedData);
      this.subClassificationLiquidityForm.patchValue(savedData, { emitEvent: false });

      // Trigger calculations after loading
      setTimeout(() => {
        this.calculateTotals();
        this.isLoadingState = false;

        // Snapshot baseline after all debounced valueChanges settle
        setTimeout(() => {
          this.stateService.markSheetInitializedWithStatus(SHEET_KEYS.subClassificationLiquidity, false);
        }, 600);
      }, 100);
    } else {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('No saved state found');
      this.isLoadingState = false;

      // Snapshot baseline after all debounced valueChanges settle
      setTimeout(() => {
        this.stateService.markSheetInitializedWithStatus(SHEET_KEYS.subClassificationLiquidity, false);
      }, 600);
    }
  }

  // TODO: cross-tab reactivity requires form rebuild on scopeOfFiling change — deferred

  /**
   * Auto-fill demo data (all column combinations)
   */
  autoFillDemo(): void {
    const demoData: any = {
      'ssmt:SubClassificationOfAssetsLiabilitiesEquityLiquidityExplanatory': 'Sub-classification of assets, liabilities and equity presented by order of liquidity as at 31 December 2024'
    };

    // Use abbreviated demo data based on column scenario
    if (this.isConsolidated && this.hasPreviousYear) {
      // 4 columns with realistic consolidated data
      this.addDemoDataForAllColumns(demoData);
    } else if (this.isConsolidated && !this.hasPreviousYear) {
      this.addDemoDataForConsolidatedOnly(demoData);
    } else if (!this.isConsolidated && this.hasPreviousYear) {
      this.addDemoDataForComparativeOnly(demoData);
    } else {
      this.addDemoDataForCurrentOnly(demoData);
    }

    this.subClassificationLiquidityForm.patchValue(demoData);
    this.calculateTotals();

    let message = 'Sample Current Year data loaded (liquidity order sub-classification)';
    if (this.isConsolidated && this.hasPreviousYear) {
      message = 'Sample Group/Company and Current/Previous Year data loaded (liquidity order sub-classification)';
    } else if (this.isConsolidated) {
      message = 'Sample Group and Company data loaded (liquidity order sub-classification - Current Year only)';
    } else if (this.hasPreviousYear) {
      message = 'Sample Current and Previous Year data loaded (liquidity order sub-classification)';
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Demo Data Loaded',
      detail: message,
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  private addDemoDataForAllColumns(data: any): void {
    // Group Current
    Object.assign(data, {
      'ssmt:LandAndBuildingsLiquidityGroupCurrent': 4000000,
      'ssmt:PlantAndMachineryLiquidityGroupCurrent': 1500000,
      'ssmt:MotorVehiclesLiquidityGroupCurrent': 300000,
      'ssmt:FurnitureFixturesAndFittingsLiquidityGroupCurrent': 150000,
      'ssmt:OfficeEquipmentLiquidityGroupCurrent': 50000,
      'ssmt:CapitalWorkInProgressLiquidityGroupCurrent': 0,
      'ssmt:ComputerSoftwareLiquidityGroupCurrent': 200000,
      'ssmt:GoodwillLiquidityGroupCurrent': 150000,
      'ssmt:BrandNamesLiquidityGroupCurrent': 30000,
      'ssmt:OtherIntangibleAssetsLiquidityGroupCurrent': 20000,
      'ssmt:RawMaterialsLiquidityGroupCurrent': 600000,
      'ssmt:WorkInProgressLiquidityGroupCurrent': 500000,
      'ssmt:FinishedGoodsLiquidityGroupCurrent': 350000,
      'ssmt:ConsumablesAndSparesLiquidityGroupCurrent': 150000,
      'ssmt:TradeReceivablesGrossAmountLiquidityGroupCurrent': 2100000,
      'ssmt:AllowanceForDoubtfulDebtsLiquidityGroupCurrent': 100000,
      'ssmt:DepositsLiquidityGroupCurrent': 100000,
      'ssmt:PrepaymentsLiquidityGroupCurrent': 80000,
      'ssmt:AccruedIncomeLiquidityGroupCurrent': 70000,
      'ssmt:TradePayablesDomesticLiquidityGroupCurrent': 800000,
      'ssmt:TradePayablesForeignLiquidityGroupCurrent': 300000,
      'ssmt:AccruedExpensesLiquidityGroupCurrent': 200000,
      'ssmt:DepositsReceivedLiquidityGroupCurrent': 100000,
      'ssmt:SundryPayablesLiquidityGroupCurrent': 50000,
      'ssmt:BankOverdraftsSubClassificationLiquidityGroupCurrent': 250000,
      'ssmt:TermLoansLiquidityGroupCurrent': 1500000,
      'ssmt:RevolvingCreditLiquidityGroupCurrent': 200000,
      'ssmt:OtherBorrowingsLiquidityGroupCurrent': 50000,
      'ssmt:OrdinarySharesLiquidityGroupCurrent': 1000000,
      'ssmt:PreferenceSharesLiquidityGroupCurrent': 0,
      'ssmt:RevaluationSurplusLiquidityGroupCurrent': 200000,
      'ssmt:ForeignCurrencyTranslationReserveLiquidityGroupCurrent': 100000,
      'ssmt:OtherCapitalReservesLiquidityGroupCurrent': 50000,
      // Group Previous (10% lower)
      'ssmt:LandAndBuildingsLiquidityGroupPrevious': 3600000,
      'ssmt:PlantAndMachineryLiquidityGroupPrevious': 1350000,
      'ssmt:MotorVehiclesLiquidityGroupPrevious': 270000,
      'ssmt:FurnitureFixturesAndFittingsLiquidityGroupPrevious': 135000,
      'ssmt:OfficeEquipmentLiquidityGroupPrevious': 45000,
      'ssmt:CapitalWorkInProgressLiquidityGroupPrevious': 0,
      'ssmt:ComputerSoftwareLiquidityGroupPrevious': 180000,
      'ssmt:GoodwillLiquidityGroupPrevious': 135000,
      'ssmt:BrandNamesLiquidityGroupPrevious': 27000,
      'ssmt:OtherIntangibleAssetsLiquidityGroupPrevious': 18000,
      'ssmt:RawMaterialsLiquidityGroupPrevious': 540000,
      'ssmt:WorkInProgressLiquidityGroupPrevious': 450000,
      'ssmt:FinishedGoodsLiquidityGroupPrevious': 315000,
      'ssmt:ConsumablesAndSparesLiquidityGroupPrevious': 135000,
      'ssmt:TradeReceivablesGrossAmountLiquidityGroupPrevious': 1890000,
      'ssmt:AllowanceForDoubtfulDebtsLiquidityGroupPrevious': 90000,
      'ssmt:DepositsLiquidityGroupPrevious': 90000,
      'ssmt:PrepaymentsLiquidityGroupPrevious': 72000,
      'ssmt:AccruedIncomeLiquidityGroupPrevious': 63000,
      'ssmt:TradePayablesDomesticLiquidityGroupPrevious': 720000,
      'ssmt:TradePayablesForeignLiquidityGroupPrevious': 270000,
      'ssmt:AccruedExpensesLiquidityGroupPrevious': 180000,
      'ssmt:DepositsReceivedLiquidityGroupPrevious': 90000,
      'ssmt:SundryPayablesLiquidityGroupPrevious': 45000,
      'ssmt:BankOverdraftsSubClassificationLiquidityGroupPrevious': 225000,
      'ssmt:TermLoansLiquidityGroupPrevious': 1350000,
      'ssmt:RevolvingCreditLiquidityGroupPrevious': 180000,
      'ssmt:OtherBorrowingsLiquidityGroupPrevious': 45000,
      'ssmt:OrdinarySharesLiquidityGroupPrevious': 1000000,
      'ssmt:PreferenceSharesLiquidityGroupPrevious': 0,
      'ssmt:RevaluationSurplusLiquidityGroupPrevious': 180000,
      'ssmt:ForeignCurrencyTranslationReserveLiquidityGroupPrevious': 90000,
      'ssmt:OtherCapitalReservesLiquidityGroupPrevious': 45000,
      // Company Current
      'ssmt:LandAndBuildingsLiquidityCompanyCurrent': 2000000,
      'ssmt:PlantAndMachineryLiquidityCompanyCurrent': 1000000,
      'ssmt:MotorVehiclesLiquidityCompanyCurrent': 200000,
      'ssmt:FurnitureFixturesAndFittingsLiquidityCompanyCurrent': 100000,
      'ssmt:OfficeEquipmentLiquidityCompanyCurrent': 30000,
      'ssmt:CapitalWorkInProgressLiquidityCompanyCurrent': 170000,
      'ssmt:ComputerSoftwareLiquidityCompanyCurrent': 100000,
      'ssmt:GoodwillLiquidityCompanyCurrent': 80000,
      'ssmt:BrandNamesLiquidityCompanyCurrent': 15000,
      'ssmt:OtherIntangibleAssetsLiquidityCompanyCurrent': 5000,
      'ssmt:RawMaterialsLiquidityCompanyCurrent': 350000,
      'ssmt:WorkInProgressLiquidityCompanyCurrent': 300000,
      'ssmt:FinishedGoodsLiquidityCompanyCurrent': 200000,
      'ssmt:ConsumablesAndSparesLiquidityCompanyCurrent': 100000,
      'ssmt:TradeReceivablesGrossAmountLiquidityCompanyCurrent': 1300000,
      'ssmt:AllowanceForDoubtfulDebtsLiquidityCompanyCurrent': 50000,
      'ssmt:DepositsLiquidityCompanyCurrent': 60000,
      'ssmt:PrepaymentsLiquidityCompanyCurrent': 40000,
      'ssmt:AccruedIncomeLiquidityCompanyCurrent': 50000,
      'ssmt:TradePayablesDomesticLiquidityCompanyCurrent': 500000,
      'ssmt:TradePayablesForeignLiquidityCompanyCurrent': 150000,
      'ssmt:AccruedExpensesLiquidityCompanyCurrent': 120000,
      'ssmt:DepositsReceivedLiquidityCompanyCurrent': 60000,
      'ssmt:SundryPayablesLiquidityCompanyCurrent': 20000,
      'ssmt:BankOverdraftsSubClassificationLiquidityCompanyCurrent': 150000,
      'ssmt:TermLoansLiquidityCompanyCurrent': 950000,
      'ssmt:RevolvingCreditLiquidityCompanyCurrent': 80000,
      'ssmt:OtherBorrowingsLiquidityCompanyCurrent': 20000,
      'ssmt:OrdinarySharesLiquidityCompanyCurrent': 1000000,
      'ssmt:PreferenceSharesLiquidityCompanyCurrent': 0,
      'ssmt:RevaluationSurplusLiquidityCompanyCurrent': 120000,
      'ssmt:ForeignCurrencyTranslationReserveLiquidityCompanyCurrent': 60000,
      'ssmt:OtherCapitalReservesLiquidityCompanyCurrent': 20000,
      // Company Previous (10% lower)
      'ssmt:LandAndBuildingsLiquidityCompanyPrevious': 1800000,
      'ssmt:PlantAndMachineryLiquidityCompanyPrevious': 900000,
      'ssmt:MotorVehiclesLiquidityCompanyPrevious': 180000,
      'ssmt:FurnitureFixturesAndFittingsLiquidityCompanyPrevious': 90000,
      'ssmt:OfficeEquipmentLiquidityCompanyPrevious': 27000,
      'ssmt:CapitalWorkInProgressLiquidityCompanyPrevious': 153000,
      'ssmt:ComputerSoftwareLiquidityCompanyPrevious': 90000,
      'ssmt:GoodwillLiquidityCompanyPrevious': 72000,
      'ssmt:BrandNamesLiquidityCompanyPrevious': 13500,
      'ssmt:OtherIntangibleAssetsLiquidityCompanyPrevious': 4500,
      'ssmt:RawMaterialsLiquidityCompanyPrevious': 315000,
      'ssmt:WorkInProgressLiquidityCompanyPrevious': 270000,
      'ssmt:FinishedGoodsLiquidityCompanyPrevious': 180000,
      'ssmt:ConsumablesAndSparesLiquidityCompanyPrevious': 90000,
      'ssmt:TradeReceivablesGrossAmountLiquidityCompanyPrevious': 1170000,
      'ssmt:AllowanceForDoubtfulDebtsLiquidityCompanyPrevious': 45000,
      'ssmt:DepositsLiquidityCompanyPrevious': 54000,
      'ssmt:PrepaymentsLiquidityCompanyPrevious': 36000,
      'ssmt:AccruedIncomeLiquidityCompanyPrevious': 45000,
      'ssmt:TradePayablesDomesticLiquidityCompanyPrevious': 450000,
      'ssmt:TradePayablesForeignLiquidityCompanyPrevious': 135000,
      'ssmt:AccruedExpensesLiquidityCompanyPrevious': 108000,
      'ssmt:DepositsReceivedLiquidityCompanyPrevious': 54000,
      'ssmt:SundryPayablesLiquidityCompanyPrevious': 18000,
      'ssmt:BankOverdraftsSubClassificationLiquidityCompanyPrevious': 135000,
      'ssmt:TermLoansLiquidityCompanyPrevious': 855000,
      'ssmt:RevolvingCreditLiquidityCompanyPrevious': 72000,
      'ssmt:OtherBorrowingsLiquidityCompanyPrevious': 18000,
      'ssmt:OrdinarySharesLiquidityCompanyPrevious': 1000000,
      'ssmt:PreferenceSharesLiquidityCompanyPrevious': 0,
      'ssmt:RevaluationSurplusLiquidityCompanyPrevious': 108000,
      'ssmt:ForeignCurrencyTranslationReserveLiquidityCompanyPrevious': 54000,
      'ssmt:OtherCapitalReservesLiquidityCompanyPrevious': 18000
    });
  }

  private addDemoDataForConsolidatedOnly(data: any): void {
    // Similar to above but only GroupCurrent and CompanyCurrent
    Object.assign(data, {
      'ssmt:LandAndBuildingsLiquidityGroupCurrent': 4000000,
      'ssmt:PlantAndMachineryLiquidityGroupCurrent': 1500000,
      'ssmt:LandAndBuildingsLiquidityCompanyCurrent': 2000000,
      'ssmt:PlantAndMachineryLiquidityCompanyCurrent': 1000000
      // ... (abbreviated for brevity)
    });
  }

  private addDemoDataForComparativeOnly(data: any): void {
    // Current and Previous (non-consolidated)
    Object.assign(data, {
      'ssmt:LandAndBuildingsLiquidityCurrent': 2000000,
      'ssmt:PlantAndMachineryLiquidityCurrent': 1000000,
      'ssmt:LandAndBuildingsLiquidityPrevious': 1800000,
      'ssmt:PlantAndMachineryLiquidityPrevious': 900000
      // ... (abbreviated for brevity)
    });
  }

  private addDemoDataForCurrentOnly(data: any): void {
    // Current only (non-consolidated, no comparative)
    Object.assign(data, {
      'ssmt:LandAndBuildingsLiquidityCurrent': 2000000,
      'ssmt:PlantAndMachineryLiquidityCurrent': 1000000,
      'ssmt:MotorVehiclesLiquidityCurrent': 200000
      // ... (abbreviated for brevity)
    });
  }

  /**
   * Clear form
   */
  clearForm(): void {
    this.subClassificationLiquidityForm.reset();
    this.messageService.add({
      severity: 'info',
      summary: 'Form Cleared',
      detail: 'All fields have been reset',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Submit form
   */
  submitForm(): void {
    if (this.reloadPromptDismissed()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Reload required',
        detail: 'Please refresh the page to obtain the latest version of this sheet.',
        life: TOAST_DURATION_LONG_MS
      });
      return;
    }

    if (this.subClassificationLiquidityForm.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields correctly',
        life: TOAST_DURATION_LONG_MS
      });
      return;
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Form submitted:', this.subClassificationLiquidityForm.value);
    this.messageService.add({
      severity: 'success',
      summary: 'Form Submitted',
      detail: 'Sub-classification (liquidity order) data saved successfully',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Check if a control is a section header
   */
  isSectionHeader(control: FormControlSchema): boolean {
    return control.type === 'SectionHeader';
  }

  /**
   * Check if a field is calculated
   */
  isCalculatedField(control: FormControlSchema): boolean {
    return control.ui.calculated === true;
  }

  /**
   * Row-type guard: a monetary, dual-column line item (the rows that own an
   * Error Description cell). Used by both the template and getRowErrorDescription.
   */
  isMonetaryItem(control: FormControlSchema): boolean {
    return control.ui.control === 'number' && control.ui.dualColumn === true;
  }

  // Developer Note (TODO): extracted-sum warning wired to the shared service. The
  // warning is currently DORMANT in production — role11's N8N snapshot is not yet
  // seeded by filing-form-container.populateSnapshotFromMbrsData and BE must return
  // snapshot data for this sheet. 
  // (Mapping Status badge column intentionally NOT added here — Error Description only.)
  private validateAgainstApiData(): void {
    const visible = resolveRole11ColumnSuffixes(this.isConsolidated, this.hasPreviousYear);
    const columns = this.ROLE11_SUFFIX_LABELS.filter((column) => (visible as readonly string[]).includes(column.key));

    const warnings = this.extractedSumWarning.computeMismatchWarnings({
      sheetKey: SHEET_KEYS.subClassificationLiquidity,
      controls: this.schema.controls,
      columns,
      getControlName: (qname, key) => qname + key,
      getCurrentValue: (name) => this.subClassificationLiquidityForm.get(name)?.value,
    });

    this.extractedSumWarning.mergeIntoValidationErrors(this.validationErrors, this.schema.controls, warnings);
  }

  /**
   * Render the Error Description cell for a monetary row. role11 only produces
   * suggested-change warnings today; the red-error vs suggested-change split is
   * kept identical to role8/role10 for forward-compat (when real errors land here).
   */
  getRowErrorDescription(control: FormControlSchema): string {
    if (!this.isMonetaryItem(control)) return '';
    const all = this.validationErrors.get(control.qname) || [];
    const suggestedChanges = all.filter((message) => message.includes(EXTRACTED_SUM_MARKER));
    const errors = all.filter((message) => !message.includes(EXTRACTED_SUM_MARKER));
    let result = '';
    if (errors.length > 0) {
      result += '<span class="error-text">ERROR</span><br>';
      result += '<span class="error-text">' + errors.map((errorText, errorIndex) => `${errorIndex + 1}. ${errorText}`).join('<br><br>') + '</span>';
    }
    if (suggestedChanges.length > 0) {
      if (result) result += '<br><br>';
      result += '<span class="error-text">*</span><span class="suggested-change-text"> SUGGESTED CHANGE</span><br>';
      result += '<span class="suggested-change-text">' + suggestedChanges.map((changeText, changeIndex) => `${changeIndex + 1}. ${changeText}`).join('<br><br>') + '</span>';
    }

    // Ctrl+Alt+B baseline debug overlay (non-production). Appended to whatever
    // real validation text this row already has (often none for a clean total).
    // baselineDebugForControl returns '' unless this row is a qualifying total.
    // Columns + getControlName rebuilt inline to mirror validateAgainstApiData's
    // computeMismatchWarnings call exactly (same visible columns + concat keys).
    if (this.baselineDebug.enabled()) {
      const visible = resolveRole11ColumnSuffixes(this.isConsolidated, this.hasPreviousYear);
      const columns = this.ROLE11_SUFFIX_LABELS.filter((column) => (visible as readonly string[]).includes(column.key));
      const baselineInfo = this.extractedSumWarning.baselineDebugForControl(
        SHEET_KEYS.subClassificationLiquidity,
        control as unknown as SummableControl,
        columns,
        (qname, key) => qname + key
      );
      if (baselineInfo) {
        result = result ? `${result}${baselineInfo}` : baselineInfo;
      }
    }

    return result;
  }

  /**
   * Get header CSS class based on level
   */
  getHeaderClass(level?: string): string {
    return level === 'h3' ? 'section-header-major' : 'section-header-minor';
  }

  /**
   * Check if form control has an error
   */
  hasError(fieldName: string): boolean {
    const control = this.subClassificationLiquidityForm.get(fieldName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  /**
   * Get error message for a form control
   */
  getErrorMessage(fieldName: string): string {
    const control = this.subClassificationLiquidityForm.get(fieldName);
    if (!control) return '';

    if (control.errors?.['required']) {
      return 'This field is required';
    }
    if (control.errors?.['min']) {
      return `Minimum value is ${control.errors['min'].min}`;
    }
    return 'Invalid value';
  }

  /**
   * Does this field's label get a `*`?
   *
   * Delegates to the shared `isControlMandatory` so this sheet answers the
   * question the same way as every other.
   *
   * **Wired even though this sheet declares ZERO mandatory controls today.**
   * That is deliberate, and it is the lesson from roles 9, 13 and 15: they
   * were skipped during the 2026-08-11 marker rollout for having no mandatory
   * fields, and when the PO's enumerated totals list flagged their calculated
   * totals hours later the flags landed with nowhere to render — the `*`
   * silently did not appear until smoke testing caught it. A sheet that
   * renders schema-driven labels gets the marker regardless of its current
   * mandatory count, so the next list cannot reopen that gap. The PO's enumerated totals are recorded per field in the sheet's spec under `docs/specifications/sheets/`.
   *
   * `crossSheetValue` is supplied so a cross-sheet `requiredIf` resolves.
   * Same-sheet `selfData` is passed only by sheets keyed by bare qname; the
   * per-cell sheets (`qname[Dimension][Period]`) deliberately omit it, since a
   * same-sheet lookup would silently miss.
   */
  isFieldRequired(control: any): boolean {
    return isControlMandatory(control, {
      crossSheetValue: (sheet, field) =>
        (this.stateService.getSheetData(sheet as any) as any)?.[field],
    });
  }

  /**
   * Memoised per-control numeric policy — this sheet's single declaration
   * point for decimals, grouping and the integer-digit cap, all resolved from
   * the control's own schema record rather than hand-written per binding.
   *
   * Memoised because templates call this once per binding inside an `*ngFor`;
   * an unmemoised `resolveNumericPolicy` would re-resolve on every
   * change-detection cycle. Keyed by `qname`, unique per control in a sheet.
   *
   * `[min]`/`[max]` are deliberately NOT bound from here — `SchemaNumericDirective`
   * assigns them onto the PrimeNG instance directly. See that directive's class
   * docblock for why a template binding coerces an unconstrained `undefined`
   * to `NaN` and silently blocks the minus key on signed monetary fields.
   */
  private policyCache = new Map<string, NumericFieldPolicy>();

  numericPolicy(control: FormControlSchema): NumericFieldPolicy {
    const cached = this.policyCache.get(control.qname);
    if (cached)
      return cached;
    const resolved = resolveNumericPolicy(control);
    this.policyCache.set(control.qname, resolved);
    return resolved;
  }

}
